import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Skeleton } from '../components/ui/skeleton';
import {
  Building2, MapPin, Info, Users, Sparkles, Image as ImageIcon,
  Check, ChevronDown, Upload, X, Loader2, AlertCircle, Trash2, Plus
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getSupportedCurrencies } from '../utils/currency';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const hasValidApiKey = GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY !== 'YOUR_GOOGLE_MAPS_API_KEY';
const libraries = ['places'];

const propertyTypes = [
  { value: 'room', label: 'Room' },
  { value: 'flat', label: 'Flat' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'house', label: 'House' },
  { value: 'villa', label: 'Villa' },
  { value: 'studio', label: 'Studio' },
  { value: 'shared_room', label: 'Shared Room' },
  { value: 'shop', label: 'Shop' },
  { value: 'office', label: 'Office Space' },
  { value: 'commercial', label: 'Commercial' },
];

const currencies = getSupportedCurrencies().slice(0, 40);

const billingPeriods = [
  { value: 'daily', label: 'Per Day' },
  { value: 'weekly', label: 'Per Week' },
  { value: 'monthly', label: 'Per Month' },
];

const furnishingOptions = [
  { value: 'furnished', label: 'Fully Furnished' },
  { value: 'semi_furnished', label: 'Semi Furnished' },
  { value: 'unfurnished', label: 'Unfurnished' },
];

const sections = [
  { id: 'type', title: 'Property Type', icon: Building2 },
  { id: 'location', title: 'Location', icon: MapPin },
  { id: 'basic', title: 'Basic Details', icon: Info },
  { id: 'occupancy', title: 'Occupancy & Rules', icon: Users },
  { id: 'facilities', title: 'Facilities', icon: Sparkles },
  { id: 'media', title: 'Media', icon: ImageIcon },
];

export default function EditProperty() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isOwner } = useAuth();
  const mapRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedSection, setExpandedSection] = useState('type');
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const [formData, setFormData] = useState({
    property_type: '',
    title: '',
    description: '',
    monthly_rent: '',
    deposit: '',
    currency: 'USD',
    billing_period: 'monthly',
    negotiable: false,
    location: {
      country: '', city: '', region: '', area: '',
      street_address: '', building_name: '', landmark: '', postal_code: '',
      latitude: null, longitude: null,
    },
    details: {
      floor_number: '', total_floors: '', room_count: 1,
      bedroom_count: 1, bathroom_count: 1, kitchen_count: 1,
      area_sqft: '', furnishing: 'unfurnished',
    },
    occupancy: {
      max_renters: 1, ideal_tenant: '',
      male_allowed: true, female_allowed: true, family_allowed: true,
      couples_allowed: true, students_allowed: true, professionals_allowed: true,
      anyone_allowed: true,
    },
    rules: {
      smoking_allowed: false, alcohol_allowed: false, pets_allowed: false,
      visitors_allowed: true, quiet_hours: '', gate_open_time: '', gate_close_time: '',
      custom_rules: '',
    },
    facilities: {
      wifi: false, water: true, electricity_backup: false,
      attached_bathroom: false, shared_bathroom: false, kitchen_access: false,
      bike_parking: false, car_parking: false, laundry: false, balcony: false,
      cctv: false, security_guard: false, elevator: false, air_conditioning: false,
      heating: false, nearby_transport: false, nearby_market: false,
      nearby_hospital: false, nearby_school: false,
    },
    media: [],
    move_in_date: '',
  });

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: hasValidApiKey ? GOOGLE_MAPS_API_KEY : '',
    libraries,
  });

  // Fetch existing property data
  useEffect(() => {
    const fetchProperty = async () => {
      if (!id || !isAuthenticated) {
        navigate('/dashboard');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/properties/${id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        });

        if (!response.ok) {
          toast.error('Property not found');
          navigate('/dashboard');
          return;
        }

        const data = await response.json();
        setFormData({
          property_type: data.property_type || '',
          title: data.title || '',
          description: data.description || '',
          monthly_rent: data.monthly_rent || '',
          deposit: data.deposit || '',
          currency: data.currency || 'USD',
          billing_period: data.billing_period || 'monthly',
          negotiable: data.negotiable || false,
          location: data.location || formData.location,
          details: data.details || formData.details,
          occupancy: data.occupancy || formData.occupancy,
          rules: data.rules || formData.rules,
          facilities: data.facilities || formData.facilities,
          media: data.media || [],
          move_in_date: data.move_in_date || '',
        });
      } catch (error) {
        console.error('Failed to fetch property:', error);
        toast.error('Failed to load property');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
    // formData is intentionally excluded: we only want to seed it from the
    // server on mount / when the property id changes. Including it would
    // cause an infinite re-fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isAuthenticated, navigate]);

  // Initialize Google Places autocomplete
  useEffect(() => {
    if (isLoaded && window.google?.maps?.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  const handleLocationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      location: { ...prev.location, [field]: value },
    }));
  };

  const handleDetailsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      details: { ...prev.details, [field]: value },
    }));
  };

  const handleOccupancyChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      occupancy: { ...prev.occupancy, [field]: value },
    }));
  };

  const handleRulesChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      rules: { ...prev.rules, [field]: value },
    }));
  };

  const handleFacilitiesChange = (field, checked) => {
    setFormData(prev => ({
      ...prev,
      facilities: { ...prev.facilities, [field]: checked },
    }));
  };

  // Street address autocomplete
  const handleStreetChange = (value) => {
    handleLocationChange('street_address', value);

    if (searchTimeout) clearTimeout(searchTimeout);

    if (value.length >= 3 && autocompleteService.current && hasValidApiKey) {
      setSearchTimeout(setTimeout(() => {
        autocompleteService.current.getPlacePredictions(
          { input: value, types: ['address', 'establishment', 'geocode'] },
          (preds, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && preds) {
              setPredictions(preds.slice(0, 5));
              setShowPredictions(true);
            }
          }
        );
      }, 300));
    } else {
      setPredictions([]);
      setShowPredictions(false);
    }
  };

  const handleSelectPrediction = (prediction) => {
    setShowPredictions(false);
    setPredictions([]);
    handleLocationChange('street_address', prediction.description);

    if (!prediction.place_id || !mapRef.current) return;

    if (!placesService.current) {
      placesService.current = new window.google.maps.places.PlacesService(mapRef.current);
    }

    placesService.current.getDetails(
      { placeId: prediction.place_id, fields: ['geometry', 'address_components', 'formatted_address'] },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry) {
          // Call .lat()/.lng() on the object directly to preserve `this` binding
          setFormData(prev => ({
            ...prev,
            location: {
              ...prev.location,
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
            },
          }));
        }
      }
    );
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files) return;

    for (let file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload only image files');
        continue;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          media: [...prev.media, { url: reader.result, type: 'image' }],
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/properties/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update property');
      }

      toast.success('Property updated successfully!');
      navigate(`/property/${id}`);
    } catch (error) {
      console.error('Error updating property:', error);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated || !isOwner) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 pb-12">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <AlertCircle className="h-12 w-12 text-amber-600 mx-auto mb-4" />
            <h1 className="text-2xl font-serif font-bold text-slate-900 mb-2">
              Access Denied
            </h1>
            <p className="text-slate-600 mb-6">You must be a property owner to edit properties.</p>
            <Button onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-6">
            <Skeleton className="h-12 w-48 mb-8" />
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-8 w-full mb-4" />
                    <Skeleton className="h-6 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const mapCenter = formData.location.latitude && formData.location.longitude
    ? { lat: formData.location.latitude, lng: formData.location.longitude }
    : { lat: 27.7172, lng: 85.324 };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-serif text-3xl md:text-4xl text-slate-900 mb-2">
              Edit Property
            </h1>
            <p className="text-slate-600 mb-8">Update your property details</p>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Property Type Section */}
            <Collapsible
              open={expandedSection === 'type'}
              onOpenChange={() => setExpandedSection(expandedSection === 'type' ? null : 'type')}
            >
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3 w-full">
                      <Building2 className="h-5 w-5 text-slate-600" />
                      <CardTitle>Property Type</CardTitle>
                      <ChevronDown className="ml-auto h-5 w-5 text-slate-600 transition-transform" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    <div className="space-y-2">
                      <Label>Property Type *</Label>
                      <Select value={formData.property_type} onValueChange={(val) => setFormData(prev => ({ ...prev, property_type: val }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select property type" />
                        </SelectTrigger>
                        <SelectContent>
                          {propertyTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Location Section */}
            <Collapsible
              open={expandedSection === 'location'}
              onOpenChange={() => setExpandedSection(expandedSection === 'location' ? null : 'location')}
            >
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3 w-full">
                      <MapPin className="h-5 w-5 text-slate-600" />
                      <CardTitle>Location</CardTitle>
                      <ChevronDown className="ml-auto h-5 w-5 text-slate-600 transition-transform" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Country *</Label>
                        <Input
                          value={formData.location.country}
                          onChange={(e) => handleLocationChange('country', e.target.value)}
                          placeholder="e.g., Nepal"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>City *</Label>
                        <Input
                          value={formData.location.city}
                          onChange={(e) => handleLocationChange('city', e.target.value)}
                          placeholder="e.g., Kathmandu"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Region</Label>
                        <Input
                          value={formData.location.region}
                          onChange={(e) => handleLocationChange('region', e.target.value)}
                          placeholder="e.g., Bagmati"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Area</Label>
                        <Input
                          value={formData.location.area}
                          onChange={(e) => handleLocationChange('area', e.target.value)}
                          placeholder="e.g., Thamel"
                        />
                      </div>
                    </div>
                    <div className="space-y-2 relative">
                      <Label>Street Address *</Label>
                      <Input
                        value={formData.location.street_address}
                        onChange={(e) => handleStreetChange(e.target.value)}
                        placeholder="Enter street address"
                      />
                      {showPredictions && predictions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg z-10 mt-1">
                          {predictions.map((pred, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleSelectPrediction(pred)}
                              className="w-full text-left px-4 py-2 hover:bg-slate-100 border-b last:border-b-0 text-sm"
                            >
                              {pred.description}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Building Name</Label>
                        <Input
                          value={formData.location.building_name}
                          onChange={(e) => handleLocationChange('building_name', e.target.value)}
                          placeholder="e.g., Rose Garden"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Landmark</Label>
                        <Input
                          value={formData.location.landmark}
                          onChange={(e) => handleLocationChange('landmark', e.target.value)}
                          placeholder="e.g., Near City Mall"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Postal Code</Label>
                        <Input
                          value={formData.location.postal_code}
                          onChange={(e) => handleLocationChange('postal_code', e.target.value)}
                          placeholder="e.g., 44600"
                        />
                      </div>
                    </div>
                    {isLoaded && (
                      <div className="h-64 rounded-lg overflow-hidden border border-slate-200">
                        <GoogleMap
                          ref={mapRef}
                          zoom={15}
                          center={mapCenter}
                          mapContainerStyle={{ width: '100%', height: '100%' }}
                        >
                          {formData.location.latitude && formData.location.longitude && (
                            <Marker position={mapCenter} />
                          )}
                        </GoogleMap>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Basic Details Section */}
            <Collapsible
              open={expandedSection === 'basic'}
              onOpenChange={() => setExpandedSection(expandedSection === 'basic' ? null : 'basic')}
            >
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3 w-full">
                      <Info className="h-5 w-5 text-slate-600" />
                      <CardTitle>Basic Details</CardTitle>
                      <ChevronDown className="ml-auto h-5 w-5 text-slate-600 transition-transform" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="e.g., Cozy Apartment in Thamel"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description *</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Describe your property..."
                        rows={4}
                      />
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Rent *</Label>
                        <Input
                          type="number"
                          value={formData.monthly_rent}
                          onChange={(e) => setFormData(prev => ({ ...prev, monthly_rent: e.target.value }))}
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Deposit</Label>
                        <Input
                          type="number"
                          value={formData.deposit}
                          onChange={(e) => setFormData(prev => ({ ...prev, deposit: e.target.value }))}
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Currency *</Label>
                        <Select value={formData.currency} onValueChange={(val) => setFormData(prev => ({ ...prev, currency: val }))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {currencies.map(c => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Billing Period</Label>
                        <Select value={formData.billing_period} onValueChange={(val) => setFormData(prev => ({ ...prev, billing_period: val }))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {billingPeriods.map(period => (
                              <SelectItem key={period.value} value={period.value}>{period.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Move-in Date</Label>
                        <Input
                          type="date"
                          value={formData.move_in_date}
                          onChange={(e) => setFormData(prev => ({ ...prev, move_in_date: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={formData.negotiable}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, negotiable: checked }))}
                      />
                      <Label>Rent is negotiable</Label>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Floor Number</Label>
                        <Input
                          type="number"
                          value={formData.details.floor_number}
                          onChange={(e) => handleDetailsChange('floor_number', e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Total Floors</Label>
                        <Input
                          type="number"
                          value={formData.details.total_floors}
                          onChange={(e) => handleDetailsChange('total_floors', e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Area (sqft)</Label>
                        <Input
                          type="number"
                          value={formData.details.area_sqft}
                          onChange={(e) => handleDetailsChange('area_sqft', e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Furnishing</Label>
                        <Select value={formData.details.furnishing} onValueChange={(val) => handleDetailsChange('furnishing', val)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {furnishingOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label>Rooms</Label>
                        <Input
                          type="number"
                          value={formData.details.room_count}
                          onChange={(e) => handleDetailsChange('room_count', parseInt(e.target.value) || 1)}
                          min="1"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Bedrooms</Label>
                        <Input
                          type="number"
                          value={formData.details.bedroom_count}
                          onChange={(e) => handleDetailsChange('bedroom_count', parseInt(e.target.value) || 1)}
                          min="1"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Bathrooms</Label>
                        <Input
                          type="number"
                          value={formData.details.bathroom_count}
                          onChange={(e) => handleDetailsChange('bathroom_count', parseInt(e.target.value) || 1)}
                          min="1"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Kitchens</Label>
                        <Input
                          type="number"
                          value={formData.details.kitchen_count}
                          onChange={(e) => handleDetailsChange('kitchen_count', parseInt(e.target.value) || 1)}
                          min="1"
                        />
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Occupancy & Rules Section */}
            <Collapsible
              open={expandedSection === 'occupancy'}
              onOpenChange={() => setExpandedSection(expandedSection === 'occupancy' ? null : 'occupancy')}
            >
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3 w-full">
                      <Users className="h-5 w-5 text-slate-600" />
                      <CardTitle>Occupancy & Rules</CardTitle>
                      <ChevronDown className="ml-auto h-5 w-5 text-slate-600 transition-transform" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    <div className="space-y-2">
                      <Label>Max Renters</Label>
                      <Input
                        type="number"
                        value={formData.occupancy.max_renters}
                        onChange={(e) => handleOccupancyChange('max_renters', parseInt(e.target.value) || 1)}
                        min="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ideal Tenant Type</Label>
                      <Input
                        value={formData.occupancy.ideal_tenant}
                        onChange={(e) => handleOccupancyChange('ideal_tenant', e.target.value)}
                        placeholder="e.g., Young professionals"
                      />
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      {[
                        { key: 'male_allowed', label: 'Males Allowed' },
                        { key: 'female_allowed', label: 'Females Allowed' },
                        { key: 'family_allowed', label: 'Families Allowed' },
                        { key: 'couples_allowed', label: 'Couples Allowed' },
                        { key: 'students_allowed', label: 'Students Allowed' },
                        { key: 'professionals_allowed', label: 'Professionals Allowed' },
                      ].map(item => (
                        <div key={item.key} className="flex items-center gap-3">
                          <Switch
                            checked={formData.occupancy[item.key]}
                            onCheckedChange={(checked) => handleOccupancyChange(item.key, checked)}
                          />
                          <Label className="cursor-pointer">{item.label}</Label>
                        </div>
                      ))}
                    </div>
                    <div className="pt-4 border-t border-slate-200">
                      <h4 className="font-semibold text-slate-900 mb-4">House Rules</h4>
                      <div className="space-y-4">
                        <div className="grid md:grid-cols-3 gap-4">
                          {[
                            { key: 'smoking_allowed', label: 'Smoking Allowed' },
                            { key: 'alcohol_allowed', label: 'Alcohol Allowed' },
                            { key: 'pets_allowed', label: 'Pets Allowed' },
                            { key: 'visitors_allowed', label: 'Visitors Allowed' },
                          ].map(item => (
                            <div key={item.key} className="flex items-center gap-3">
                              <Switch
                                checked={formData.rules[item.key]}
                                onCheckedChange={(checked) => handleRulesChange(item.key, checked)}
                              />
                              <Label className="cursor-pointer">{item.label}</Label>
                            </div>
                          ))}
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Quiet Hours</Label>
                            <Input
                              value={formData.rules.quiet_hours}
                              onChange={(e) => handleRulesChange('quiet_hours', e.target.value)}
                              placeholder="e.g., 10 PM - 8 AM"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Gate Closing Time</Label>
                            <Input
                              type="time"
                              value={formData.rules.gate_close_time}
                              onChange={(e) => handleRulesChange('gate_close_time', e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Custom Rules</Label>
                          <Textarea
                            value={formData.rules.custom_rules}
                            onChange={(e) => handleRulesChange('custom_rules', e.target.value)}
                            placeholder="Any additional house rules..."
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Facilities Section */}
            <Collapsible
              open={expandedSection === 'facilities'}
              onOpenChange={() => setExpandedSection(expandedSection === 'facilities' ? null : 'facilities')}
            >
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3 w-full">
                      <Sparkles className="h-5 w-5 text-slate-600" />
                      <CardTitle>Facilities</CardTitle>
                      <ChevronDown className="ml-auto h-5 w-5 text-slate-600 transition-transform" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0">
                    <div className="grid md:grid-cols-3 gap-4">
                      {[
                        { key: 'wifi', label: 'WiFi' },
                        { key: 'water', label: 'Water Supply' },
                        { key: 'electricity_backup', label: 'Electricity Backup' },
                        { key: 'attached_bathroom', label: 'Attached Bathroom' },
                        { key: 'shared_bathroom', label: 'Shared Bathroom' },
                        { key: 'kitchen_access', label: 'Kitchen Access' },
                        { key: 'bike_parking', label: 'Bike Parking' },
                        { key: 'car_parking', label: 'Car Parking' },
                        { key: 'laundry', label: 'Laundry Facility' },
                        { key: 'balcony', label: 'Balcony' },
                        { key: 'cctv', label: 'CCTV' },
                        { key: 'security_guard', label: 'Security Guard' },
                        { key: 'elevator', label: 'Elevator' },
                        { key: 'air_conditioning', label: 'Air Conditioning' },
                        { key: 'heating', label: 'Heating' },
                        { key: 'nearby_transport', label: 'Near Public Transport' },
                        { key: 'nearby_market', label: 'Near Market' },
                        { key: 'nearby_hospital', label: 'Near Hospital' },
                        { key: 'nearby_school', label: 'Near School' },
                      ].map(item => (
                        <div key={item.key} className="flex items-center gap-3">
                          <Switch
                            checked={formData.facilities[item.key]}
                            onCheckedChange={(checked) => handleFacilitiesChange(item.key, checked)}
                          />
                          <Label className="cursor-pointer">{item.label}</Label>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Media Section */}
            <Collapsible
              open={expandedSection === 'media'}
              onOpenChange={() => setExpandedSection(expandedSection === 'media' ? null : 'media')}
            >
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3 w-full">
                      <ImageIcon className="h-5 w-5 text-slate-600" />
                      <CardTitle>Media ({formData.media.length})</CardTitle>
                      <ChevronDown className="ml-auto h-5 w-5 text-slate-600 transition-transform" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                      <input
                        type="file"
                        id="media-upload"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <label htmlFor="media-upload" className="cursor-pointer block">
                        <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm font-medium text-slate-900">Click to upload images</p>
                        <p className="text-xs text-slate-500">PNG, JPG, GIF up to 10MB</p>
                      </label>
                    </div>

                    {formData.media.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <AnimatePresence>
                          {formData.media.map((media, idx) => (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              className="relative group"
                            >
                              <img
                                src={media.url}
                                alt={`Preview ${idx}`}
                                className="w-full h-32 object-cover rounded-lg"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(idx)}
                                className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Submit Button */}
            <div className="flex gap-4 pt-8">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/property/${id}`)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-accent hover:bg-accent/90"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
